import { instanceCountKey } from '@bmo/form-core';

import type { WorkflowStep } from '@/api/workflows';

import type { FieldValue } from './FieldRenderer';

/**
 * `resetStepId` (opt-in): the step a choice field wipes clean when its answer changes.
 *
 * ## Why this exists on top of `clearHiddenAnswers`
 *
 * `clearAnswersHiddenBy` can only clear what the controller HIDES — a step, section or
 * field whose own conditions name it. RDSP's "Who is filling out this form" hides almost
 * nothing on Beneficiary Details: the step's Personal Details and Address blocks are
 * visible under BOTH answers and merely change ownership — prefilled-and-locked from the
 * Accountholder step when the beneficiary IS the holder, typed by hand when they are not
 * (their `disableConditions` name the controller, not their `conditions`). So swapping the
 * answer left the other branch's data sitting on the step: the holder's name, SIN and
 * address, now editable and reading as if the advisor had typed them for the beneficiary.
 *
 * Rather than restate the branch on all 18 fields, the controller names the step and the
 * whole step goes back to how it looked before anyone reached it.
 *
 * ## "Reset", not just "cleared"
 *
 * Dropping the answers is only half of it. A step the advisor has already been shown is
 * frozen against cross-step prefill (`shownStepsRef`) and against defaults
 * (`applyDefaultOptions`), so a bare wipe would leave the beneficiary's locked mirror
 * fields permanently blank. FormShell therefore also UNFREEZES the step — see the
 * `resetStepId` branch in its `onChange` — which is what makes the prefill and default
 * passes refill it on the very next render, exactly as a first visit would.
 *
 * Everything on the step goes, including answers no condition touches. That is the point:
 * the config says "this step is a function of my answer", and a per-field exemption list
 * would be the thing it exists to avoid.
 */

/**
 * `inst:<sectionId>:<index>:<fieldId>` — the key a field inside a repeatable section answers
 * under. See `qualifiedKey`.
 */
const INSTANCE_KEY = /^inst:[^:]+:\d+:(.+)$/;

/**
 * The step `fieldId` resets, or null when it carries no `resetStepId` (the default).
 *
 * `fieldId` may arrive as an instance-qualified key: a driver inside a repeatable section is
 * authored ONCE but answered once per instance, and `onChange` reports the qualified key.
 * Every instance shares the one field, so EITHER instance's answer resets the named step —
 * which is the point for RDSP's "Are they the primary caregiver of the beneficiary?", asked
 * per accountholder and owning a Primary Caregiver step prefilled from whichever one says yes.
 */
export function resetStepFor(steps: readonly WorkflowStep[], fieldId: string): WorkflowStep | null {
  const baseId = INSTANCE_KEY.exec(fieldId)?.[1] ?? fieldId;
  const owner = steps.find((s) =>
    (s.sections ?? []).some((sec) =>
      sec.rows.some((r) => r.columns.some((c) => c.field?.id === baseId)),
    ),
  );
  const controller = (owner?.sections ?? [])
    .flatMap((sec) => sec.rows)
    .flatMap((r) => r.columns)
    .find((c) => c.field?.id === baseId)?.field;
  const targetId = controller?.resetStepId;
  if (!targetId) return null;
  // A step that resets ITSELF would wipe the answer that triggered it, which reads as the
  // form eating the click. Refused here rather than at the BE, which validates one field and
  // cannot see which step holds it.
  if (owner && targetId === owner.id) return null;
  // A dangling id (the step was deleted, or a snapshot was seeded without remapping it)
  // resets nothing rather than throwing — the config is an optimisation of hand-written
  // conditions, never load-bearing for correctness.
  return steps.find((s) => s.id === targetId) ?? null;
}

/**
 * Every values-map key the step owns: the bare field ids of its plain sections, and for a
 * repeatable section every `inst:<sectionId>:…` answer plus the `__instanceCount:` sentinel
 * that counts them, so the block returns at its floor instead of holding the same number of
 * blank instances (the same reasoning as `clearAnswersHiddenBy`).
 */
export function stepAnswerKeys(step: WorkflowStep, values: Record<string, FieldValue>): string[] {
  const keys: string[] = [];
  const instancePrefixes: string[] = [];
  for (const section of step.sections ?? []) {
    if (section.repeatable) {
      instancePrefixes.push(`inst:${section.id}:`);
      keys.push(instanceCountKey(section.id));
      continue;
    }
    for (const row of section.rows) {
      for (const col of row.columns) {
        if (col.field) keys.push(col.field.id);
      }
    }
  }
  if (instancePrefixes.length > 0) {
    for (const key of Object.keys(values)) {
      if (instancePrefixes.some((p) => key.startsWith(p))) keys.push(key);
    }
  }
  return keys;
}

/**
 * Drops every answer on `step`. Returns `values` untouched when the step held none, so the
 * common case (a controller whose answer changed but whose target was never filled) costs
 * no re-render.
 */
export function clearStepAnswers(
  step: WorkflowStep,
  values: Record<string, FieldValue>,
): Record<string, FieldValue> {
  let next = values;
  for (const key of stepAnswerKeys(step, values)) {
    if (!(key in values)) continue;
    if (next === values) next = { ...values };
    delete next[key];
  }
  return next;
}
